import { useEffect, useRef } from 'react';
import { FAMILY_LABEL } from '../data/families.js';
import { EFFECT_KIND, TARGET_KIND } from '../data/disasters.js';
// Advanced lookup is a superset of the Classic plants (same names)
// plus the storage plants, so it names targets in both modes.
import { ADVANCED_PLANT_BY_ID as PLANT_BY_ID } from '../data/advancedCapacity.js';
import { RISK_TIER, RISK_TIER_LABELS } from '../data/riskOutlook.js';
import { ADVANCED_DISASTER_EFFECTS } from '../data/advancedDisasters.js';
import { ADVANCED_UNITS } from '../data/advancedCapacity.js';
import { demandAddAmountForEffect } from '../engine/disasters.js';
import { firmFactorFromGenerationFactor } from '../engine/advancedCapacity.js';

function riskTierClass(tier) {
  return `build-news-panel-risk-${tier ?? RISK_TIER.STANDARD}`;
}

function displayRiskTier({ showRiskTiers, tier }) {
  return showRiskTiers ? tier : RISK_TIER.LOW;
}

function describeTarget(disaster) {
  const target = disaster.target;
  switch (target.kind) {
    case TARGET_KIND.PLANTS:
      return target.ids.map((id) => PLANT_BY_ID[id]?.name ?? id).join(', ');
    case TARGET_KIND.FAMILY:
      return `${FAMILY_LABEL[target.family]} family`;
    case TARGET_KIND.ALL_VARIABLE:
      return 'Variable plants';
    case TARGET_KIND.LARGEST_NON_NUCLEAR_FAMILY:
      return 'Largest non-nuclear family';
    case TARGET_KIND.LARGEST_FAMILY:
      return 'Largest family';
    case TARGET_KIND.PLAYER:
      return '';
    default:
      return 'Affected system';
  }
}

function describeEffect(effect, year = null) {
  switch (effect.kind) {
    case EFFECT_KIND.OUTPUT_MULTIPLIER:
      if (effect.factorsByPlantId) {
        return Object.entries(effect.factorsByPlantId)
          .map(([id, factor]) => {
            const plant = PLANT_BY_ID[id]?.name ?? id;
            if (factor === 0) return `${plant} output offline`;
            return `${plant} output reduced by ${Math.round((1 - factor) * 100)}%`;
          })
          .join('; ');
      }
      if (effect.factor === 0) return 'output offline';
      return (
        `output reduced by ${Math.round((1 - effect.factor) * 100)}%` +
        (effect.persistent ? ', permanently' : '')
      );
    case EFFECT_KIND.OPEX_MULTIPLIER:
      return `opex increases to ${Math.round(effect.factor * 100)}%`;
    case EFFECT_KIND.DEMAND_ADD:
      if (effect.amountByYear) {
        return `demand +${demandAddAmountForEffect(effect, year)} TWh`;
      }
      return `demand +${effect.amount} TWh`;
    case EFFECT_KIND.SUBSIDY_REMOVE:
      return 'fossil fuel subsidy ends';
    default:
      return 'system effect';
  }
}

function describeFirmFactor(factor) {
  if (factor <= 0) return 'firm capacity offline';
  return `firm capacity reduced by ${Math.round((1 - factor) * 100)}%`;
}

// The firm capacity channel a card carries in Advanced Mode, as the
// player sees it. Mirrors computeAdvancedDisasterEffects: softened to
// (1 + generationFactor) / 2 by default, unless the card overrides
// with a fixed factor or reuses the generation factor outright.
function firmFactorForDisplay(generationFactor, adv) {
  if (adv.firmUsesGenerationFactor) return generationFactor;
  if (adv.firmCapacityFactor !== undefined) return adv.firmCapacityFactor;
  return firmFactorFromGenerationFactor(generationFactor);
}

function describeFirmChannel(disaster, adv) {
  if (disaster.effect?.kind !== EFFECT_KIND.OUTPUT_MULTIPLIER) return null;
  if (adv.affectsFirmCapacity === false) return null;

  // Per-plant generation factors (Nuclear Public Outcry) would need a
  // clause each; the shape is what matters, and it is exact.
  if (disaster.effect.factorsByPlantId || disaster.severity?.mild?.factorsByPlantId) {
    return 'firm capacity falls in step with output';
  }

  const factors = disaster.twoTierSeverity
    ? [disaster.severity.mild.factor, disaster.severity.severe.factor]
    : [disaster.effect.factor];
  const firmFactors = [
    ...new Set(factors.map((factor) => firmFactorForDisplay(factor, adv)))
  ];
  if (firmFactors.every((factor) => factor >= 1)) return null;
  if (firmFactors.length === 1) return describeFirmFactor(firmFactors[0]);
  const [mild, severe] = firmFactors;
  if (severe <= 0) return `${describeFirmFactor(mild)} or offline`;
  return (
    `${describeFirmFactor(mild)} or ${Math.round((1 - severe) * 100)}%`
  );
}

// firmDerate is a firm-capacity-only hit applied by family, independent
// of the card's Classic effect kind, so nothing else in this panel
// would ever surface it.
function describeFirmDerates(adv) {
  const derates = adv.firmDerate
    ? Array.isArray(adv.firmDerate)
      ? adv.firmDerate
      : [adv.firmDerate]
    : [];
  return derates.map((derate) => {
    const families = (derate.families ?? [])
      .map((family) => FAMILY_LABEL[family] ?? family)
      .join(', ');
    return `${families} ${describeFirmFactor(derate.factor)}`;
  });
}

function describeRiskInfo(disaster, year, advanced) {
  const target = describeTarget(disaster);
  const adv = advanced ? ADVANCED_DISASTER_EFFECTS[disaster.id] ?? {} : {};

  // Advanced Mode: cards that touch only the firm capacity channel
  // (e.g. Battery Safety Recall) are not output hits.
  const generationClauses =
    disaster.effect?.kind === EFFECT_KIND.OUTPUT_MULTIPLIER &&
    adv.affectsGeneration === false
      ? []
      : disaster.twoTierSeverity
        ? [
            [disaster.severity.mild, disaster.severity.severe]
              .map((effect) => describeEffect(effect, year))
              .join(' or ')
          ]
        : [describeEffect(disaster.effect, year)];

  const clauses = [...generationClauses];
  if (advanced) {
    const firmClause = describeFirmChannel(disaster, adv);
    if (firmClause) clauses.push(firmClause);
  }
  const body = target ? `${target}: ${clauses.join('; ')}` : clauses.join('; ');

  // Peak demand and the family derates are system-wide, so they sit
  // outside the target prefix rather than reading as effects on it.
  const systemClauses = advanced
    ? [
        ...(adv.peakDemandAddByYear && adv.peakDemandAddByYear[year]
          ? [`peak demand +${adv.peakDemandAddByYear[year]} ${ADVANCED_UNITS.capacity}`]
          : []),
        ...describeFirmDerates(adv)
      ]
    : [];

  return [body, ...systemClauses].filter(Boolean).join('. ');
}

export function GlobalNewsPanel({
  id,
  year,
  drawCount,
  disasters,
  riskTierByDisasterId = {},
  advanced = false,
  onClose
}) {
  const ref = useRef(null);

  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape') onClose?.();
    }
    function handleClick(e) {
      if (e.target.closest?.('[data-news-toggle="true"]')) {
        return;
      }
      if (ref.current && !ref.current.contains(e.target)) {
        onClose?.();
      }
    }
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [onClose]);

  const sorted = [...disasters].sort((a, b) => a.name.localeCompare(b.name));
  const showRiskTiers = year > 1 && Object.keys(riskTierByDisasterId).length > 0;

  return (
    <div
      ref={ref}
      id={id}
      role="dialog"
      aria-label={`Risk Outlook, year ${year}`}
      className="build-news-panel"
      data-testid="global-news-panel"
    >
      <div className="build-news-panel-head">
        <div className="build-news-panel-eyebrow">Risk Outlook, Year {year}</div>
        <div className="build-news-panel-sub">
          Risk assessment for incoming disruptions
        </div>
      </div>
      {sorted.length === 0 ? (
        <p className="build-news-panel-empty">
          No further disruptions remain in the risk landscape.
        </p>
      ) : (
        <ul className="build-news-panel-list">
          {sorted.map((d) => {
            const tier = displayRiskTier({
              showRiskTiers,
              tier: riskTierByDisasterId[d.id] ?? RISK_TIER.STANDARD
            });
            const tierLabel = RISK_TIER_LABELS[tier];
            return (
              <li
                key={d.id}
                className="build-news-panel-row"
                data-testid={`global-news-row-${d.id}`}
              >
                <span className="build-news-panel-name" tabIndex={0}>
                  {d.name}
                </span>
                <span
                  className={`build-news-panel-pct ${riskTierClass(tier)}`}
                >
                  {tierLabel}
                </span>
                <span className="build-news-panel-info" role="tooltip">
                  {describeRiskInfo(d, year, advanced)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
